<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="选择展示图"
    width="700px"
    center
    :close-on-click-modal="false"
    @close="handleClose"
    custom-class="pay-image-picker-dialog"
  >

    <!-- 分类 tabs -->
    <div class="tabs-block">
      <span
        class="tab-item"
        :class="{ 'tab-active': currentTab === -1 }"
        @click="switchTab(-1)"
      >
        全部
      </span>
      <span
        v-for="(cat, idx) in categories"
        :key="cat.id"
        class="tab-item"
        :class="{ 'tab-active': currentTab === idx }"
        @click="switchTab(idx)"
      >
        {{ cat.categoryName }}
      </span>
    </div>

    <!-- 图片网格 -->
    <el-scrollbar class="image-scroll">
      <!-- 空状态 -->
      <div v-if="!loading && currentImages.length === 0" class="empty-state">
        暂无图片
      </div>
      <!-- 加载中 -->
      <div v-if="loading" class="loading-in-grid">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <!-- 图片列表 -->
      <div v-else-if="currentImages.length > 0" class="image-grid">
        <div
          v-for="img in currentImages"
          :key="img.id"
          class="image-cell"
          :class="{ 'image-cell-selected': selectedImageUrl === img.url }"
          @click="selectImage(img.url)"
        >
          <el-image
            :src="img.url"
            fit="cover"
            lazy
            :preview-src-list="previewList"
            :initial-index="previewIndex(img.url)"
          ></el-image>
          <div v-if="selectedImageUrl === img.url" class="select-badge">
            使用
          </div>
        </div>
      </div>
    </el-scrollbar>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleCustomUpload">自定义上传</el-button>
      <el-button type="primary" @click="handleConfirm">确认选择</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getPayDisplayList } from "@/utils/api/activity";

export default {
  name: "PayImagePicker",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    currentImage: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      categories: [],
      currentTab: 0,
      selectedImageUrl: "",
      loading: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        // controlled by parent
      },
    },
    currentImages() {
      if (this.categories.length === 0) return [];
      if (this.currentTab === -1) {
        return this.categories.flatMap(cat => cat.images || []);
      }
      const current = this.categories[this.currentTab];
      return current && current.images ? current.images : [];
    },
    // 预览图片列表（当前分类所有图片 URL）
    previewList() {
      return this.currentImages.map(img => img.url);
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.onOpen();
      } else {
        this.loading = false;
      }
    },
    currentImage(val) {
      this.selectedImageUrl = val || "";
    },
  },
  methods: {
    onOpen() {
      if (this.loading) return;
      this.loading = true;
      this.currentTab = -1;
      this.categories = [];
      getPayDisplayList()
        .then((res) => {
          if (res.status === 1 && res.data && res.data.length > 0) {
            this.categories = res.data;
          }
          this.selectedImageUrl = this.currentImage || "";
          this.loading = false;
        })
        .catch(() => {
          this.$message.error("获取图片失败");
          this.loading = false;
        });
    },
    switchTab(idx) {
      this.currentTab = idx;
      this.selectedImageUrl = "";
    },
    selectImage(url) {
      if (this.selectedImageUrl === url) {
        this.selectedImageUrl = "";
      } else {
        this.selectedImageUrl = url;
      }
    },
    // 获取当前图片在预览列表中的索引
    previewIndex(url) {
      return this.currentImages.findIndex(img => img.url === url);
    },
    handleConfirm() {
      if (!this.selectedImageUrl) {
        this.$message.warning("请选择图片");
        return;
      }
      this.$emit("confirm", this.selectedImageUrl);
      this.$emit("close");
      this.selectedImageUrl = "";
    },
    handleCustomUpload() {
      this.$emit("customUpload");
      this.$emit("close");
    },
    handleClose() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped lang="scss">
.tabs-block {
  width: 90%;
  margin: 0 auto 10px;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding-bottom: 5px;

  /* 隐藏滚动条 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  .tab-item {
    display: inline-block;
    padding: 10px 12px;
    position: relative;
    font-weight: bold;
    color: #798497;
    cursor: pointer;
    font-size: 14px;

    &.tab-active {
      color: #323b5a;

      &::after {
        content: "";
        width: 15px;
        height: 4px;
        border-radius: 2px;
        background-color: #1877ff;
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }
}

.image-scroll {
  height: 500px;
  padding: 10px 0 10px 20px;
  box-sizing: border-box;
}

.image-scroll ::v-deep .el-scrollbar__wrap {
  overflow-x: hidden;
}

.image-scroll ::v-deep .el-scrollbar__view {
  padding-right: 10px;
}

/* 空状态 */
.empty-state {
  width: 100%;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #a7adc6;
}

/* 网格内 loading */
.loading-in-grid {
  width: 100%;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #a7adc6;
}

.loading-in-grid i {
  font-size: 30px;
  margin-bottom: 10px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.image-cell {
  aspect-ratio: 5 / 4;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s;

  ::v-deep .el-image {
    width: 100%;
    height: 100%;
  }

  .select-badge {
    position: absolute;
    right: 0;
    bottom: 0;
    padding: 3px 8px;
    font-size: 12px;
    color: #fff;
    background: linear-gradient(to right, #5356ff, #843fff);
    border-top-left-radius: 8px;
  }

  &.image-cell-selected {
    border-color: #1877ff;
  }
}
</style>

<style lang="scss">
.pay-image-picker-dialog {
  .el-dialog__body {
    padding: 10px 20px 20px;
  }
  .el-dialog__footer {
    padding: 10px 20px 20px;
  }
}
</style>
